// The slide master (SC, Features 4, 26 Oct 2026, David: "Slide master: edit a layout once (logo, footer, title style)
// and every slide using it follows"). A deck keeps, in its own deck entry (one write a change, one step Undo takes back
// whole), the master's own look for every slide (`all`: the title's and the text's look on every layout, boxes behind
// every slide, a background) and each of the board's layouts' (`layouts`: where its fields sit and how they look, its
// boxes, its background). A deck's own layouts (S6) are edited in their own place in `DeckData.layouts`.
//
// A slide follows its layout and the master piece by piece, as PowerPoint's placeholders do: whatever the slide set
// itself wins (a title it moved stays where it was put, a colour it gave its words stays), and the rest comes from its
// layout, then the master. Reset slide takes the slide's own away, so it follows again. A slide made from one of the
// deck's own layouts was given a copy of that layout's pieces (S6); the first time the layout is edited here it keeps
// what it was (`was`), so a slide's copy that still matches it follows the layout too, and one changed by hand stays.
//
// Nothing here is drawn or migrated: a deck with no master edits draws exactly as before. The board draws a slide
// through resolveSlide (the slide face, src/boards/slides/Face.tsx), and so does the PowerPoint file.

import { FIELDS, cleanBg, cleanBoxes, cleanPlace, type FieldName, type FieldPlace, type SlideBg, type SlideBox, type SlidePlace } from "./slideDesign";
import type { SavedLayout } from "./deckTheme";
import type { SlideData, SlideLayout } from "./slides";

export interface LayoutMaster {
  /** where the layout's fields sit and how they look (on the master: their look only, on every layout) */
  place?: SlidePlace;
  /** boxes behind every slide on it, as PowerPoint's master and layout shapes: never selected on a slide */
  boxes?: SlideBox[];
  /** its background, under a slide's own */
  bg?: SlideBg | null;
}

export interface DeckMaster {
  /** the master: on every slide */
  all?: LayoutMaster;
  /** each of the board's layouts */
  layouts?: Partial<Record<SlideLayout, LayoutMaster>>;
}

/** What one master piece is: the master, one of the board's layouts, or one of the deck's own by id. */
export type MasterKey = "all" | SlideLayout | `L${string}`;

const LAYOUT_IDS: SlideLayout[] = ["title", "section", "text", "two", "big", "quote", "picture", "full", "widget", "blank"];
/** The most boxes the master and one layout hold, each. */
export const MAX_MASTER_BOXES = 40;

/** One master piece as kept, or null when it changes nothing. */
export function cleanLayoutMaster(raw: unknown): LayoutMaster | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const place = cleanPlace(r.place);
  // a live widget is the board's data, not a layout's: the master never carries one
  const boxes = cleanBoxes(r.boxes)
    .filter((b) => b.kind !== "widget")
    .slice(0, MAX_MASTER_BOXES);
  const bg = cleanBg(r.bg);
  const out: LayoutMaster = { ...(Object.keys(place).length ? { place } : {}), ...(boxes.length ? { boxes } : {}), ...(bg ? { bg } : {}) };
  return Object.keys(out).length ? out : null;
}

/** A deck's master as kept, or null when nothing in it changes a slide. */
export function cleanMaster(raw: unknown): DeckMaster | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const all = cleanLayoutMaster(r.all);
  const layouts: Partial<Record<SlideLayout, LayoutMaster>> = {};
  const lr = r.layouts && typeof r.layouts === "object" && !Array.isArray(r.layouts) ? (r.layouts as Record<string, unknown>) : {};
  for (const id of LAYOUT_IDS) {
    const m = cleanLayoutMaster(lr[id]);
    if (m) layouts[id] = m;
  }
  const out: DeckMaster = { ...(all ? { all } : {}), ...(Object.keys(layouts).length ? { layouts } : {}) };
  return Object.keys(out).length ? out : null;
}

/** Whether a master piece key names one of the board's layouts. */
export const isLayoutKey = (k: string): k is SlideLayout => (LAYOUT_IDS as string[]).includes(k);

// ---------------------------------------------------------------------------
// a slide as it follows its layout and the master

/** A field's position, kept as one: a field moved takes all of it from where it was moved. */
const POSITION = ["x", "y", "w", "h", "rot"] as const;
const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** A field's place laid over another's: each look the top one set wins, its position wins as one when it has one. */
function over(under: FieldPlace | undefined, top: FieldPlace | undefined, lookOnly = false): FieldPlace | undefined {
  if (!under) return top;
  if (!top) return lookOnly ? withoutPos(under) : under;
  const base = lookOnly ? withoutPos(under) : { ...under };
  const out: FieldPlace = { ...base };
  if (top.x !== undefined) for (const k of POSITION) delete (out as Record<string, unknown>)[k];
  for (const [k, v] of Object.entries(top)) if (v !== undefined) (out as Record<string, unknown>)[k] = v;
  return out;
}

function withoutPos(p: FieldPlace): FieldPlace {
  const out = { ...p } as Record<string, unknown>;
  for (const k of [...POSITION, "z", "hidden"]) delete out[k];
  return out as FieldPlace;
}

/** The master's look for a field: the master's title look on every layout's title, its text look on every text. */
function masterLook(all: LayoutMaster | undefined, f: FieldName): FieldPlace | undefined {
  const p = all?.place;
  if (!p) return undefined;
  const own = p[f] ? withoutPos(p[f]!) : undefined;
  if (own && Object.keys(own).length) return own;
  if (f === "body2" && p.body) return withoutPos(p.body);
  return undefined;
}

/** A deck's own layout's pieces as slides made from it were given them: what it was when first edited here, else what it is. */
export function layoutBase(l: SavedLayout & { was?: LayoutMaster }): { place?: SlidePlace; boxes?: SlideBox[]; bg?: SlideBg | null } {
  return l.was ? { place: l.was.place, boxes: l.was.boxes, bg: l.was.bg } : { place: l.place, boxes: l.boxes, bg: l.bg };
}

/** A box's look and place without the words and layer a slide gives it: what decides whether it still matches its layout's. */
const boxShape = (b: SlideBox) => {
  const { id: _i, z: _z, text: _t, ...rest } = b;
  return rest;
};

/**
 * A slide as it is drawn: its own pieces over its layout's and the master's. `skip` leaves a piece out (the master view
 * draws the piece being edited from the slide it is edited on). Boxes from the master and the layout come back in
 * `under`, drawn behind everything on the slide and never selected there; a slide that hides background graphics
 * (`noMaster`) has none.
 */
export function resolveSlide(s: SlideData, deck: { master?: DeckMaster | null; layouts?: (SavedLayout & { was?: LayoutMaster })[] }, skip?: MasterKey): SlideData & { under?: SlideBox[] } {
  // the master itself is edited as it is: nothing under it
  if (skip === "all") return s;
  const master = deck.master ?? null;
  const own = s.lay ? (deck.layouts ?? []).find((l) => l.id === s.lay) : undefined;
  const all = master?.all;
  const lay = own ? undefined : skip === s.layout ? undefined : master?.layouts?.[s.layout];
  // a deck's own layout edited here: the slide's copies that still match what it was follow what it is now
  const fromOwn = own && own.was && skip !== own.id ? ownFollow(s, own) : null;
  if (!all && !lay && !fromOwn) return s;
  const src = fromOwn ?? s;
  const place: SlidePlace = {};
  for (const f of FIELDS) {
    let p = masterLook(all, f);
    p = over(p, lay?.place?.[f]);
    p = over(p, src.place?.[f]);
    if (p && Object.keys(p).length) place[f] = p;
  }
  const under = s.noMaster ? [] : [...(all?.boxes ?? []).map((b) => ({ ...b, id: `m-${b.id}`.slice(0, 40) })), ...(lay?.boxes ?? []).map((b) => ({ ...b, id: `y-${b.id}`.slice(0, 40) }))];
  const bg = src.bg ?? lay?.bg ?? all?.bg ?? null;
  const { place: _p, bg: _b, ...rest } = src;
  return { ...rest, ...(Object.keys(place).length ? { place } : {}), ...(bg ? { bg } : {}), ...(under.length ? { under } : {}) };
}

/** A slide made from a deck's own layout, its untouched copies of the layout's pieces brought up to what the layout is now. */
function ownFollow(s: SlideData, l: SavedLayout & { was?: LayoutMaster }): SlideData {
  const was = layoutBase(l);
  // fields: a field's place still as the layout first had it follows the layout's place now
  const place: SlidePlace = { ...(s.place ?? {}) };
  for (const f of FIELDS) {
    if (same(s.place?.[f], was.place?.[f])) {
      if (l.place?.[f]) place[f] = l.place[f];
      else delete place[f];
    }
  }
  // boxes: one still shaped as the layout first put it takes the layout's shape now, with the slide's words and layer
  const nowBy = new Map((l.boxes ?? []).map((b) => [`${l.id}-${b.id}`.slice(0, 40), b]));
  const wasBy = new Map((was.boxes ?? []).map((b) => [`${l.id}-${b.id}`.slice(0, 40), b]));
  const seen = new Set<string>();
  const boxes: SlideBox[] = [];
  for (const b of s.boxes ?? []) {
    const w = wasBy.get(b.id);
    if (!w || !same(boxShape(b), boxShape({ ...w, id: b.id }))) {
      boxes.push(b);
      continue;
    }
    seen.add(b.id);
    const n = nowBy.get(b.id);
    // taken off the layout since: gone from the slides that never changed it
    if (n) boxes.push({ ...n, id: b.id, z: b.z, ...(b.text ? { text: b.text } : {}) });
  }
  // boxes the layout gained since go behind the slide's own
  const low = Math.min(0, ...boxes.map((b) => b.z));
  let k = 0;
  for (const [id, n] of nowBy) if (!wasBy.has(id) && !seen.has(id) && !boxes.some((b) => b.id === id)) boxes.unshift({ ...n, id, z: low - 1 - k++ });
  const bg = same(s.bg ?? null, was.bg ?? null) ? (l.bg ?? null) : (s.bg ?? null);
  return { ...s, place, boxes, bg };
}

/** The fields the master or a layout places on this slide (not the slide itself): moving another field leaves these be. */
export function inheritedPlaced(s: SlideData, deck: { master?: DeckMaster | null }): Set<FieldName> {
  const out = new Set<FieldName>();
  if (s.lay) return out;
  const lay = deck.master?.layouts?.[s.layout];
  for (const f of FIELDS) {
    const p = lay?.place?.[f];
    if (p && p.x !== undefined && !(s.place?.[f]?.x !== undefined)) out.add(f);
  }
  return out;
}

/** How many slides each master piece reaches: the master every slide, a layout the slides on it. */
export function masterUse(slides: SlideData[]): Map<string, number> {
  const out = new Map<string, number>([["all", slides.length]]);
  for (const s of slides) {
    const k = s.lay ?? s.layout;
    out.set(k, (out.get(k) ?? 0) + 1);
  }
  return out;
}
